import { InfoIcon } from 'lucide-react'
import { useState } from 'react'

import { useSendManualReminder } from '@/api/assessor-questionnaire.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'

import { Alert, AlertIcon } from '@/components/lemmala-design-system/alert/alert'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { UserBadge } from '@/components/shared/user-badge/user-badge'

interface SendReminderModalProps {
  open: boolean
  onClose: () => void
  companyId: Company['id']
  recipientsEmails: string[]
}

export const SendReminderModal = ({
  open,
  onClose,
  companyId,
  recipientsEmails,
}: SendReminderModalProps) => {
  const [selectedEmails, setSelectedEmails] = useState<string[]>(recipientsEmails)
  const { toast, dismiss } = useToast()
  const { mutateAsync: sendReminder } = useSendManualReminder()

  const addEmail = (email: string) => {
    dismiss()
    setSelectedEmails((prev) => [...prev, email])
    toast({
      variant: 'success',
      title: 'Recipient restored.',
    })
  }

  const removeEmail = (email: string) => {
    setSelectedEmails((prev) => prev.filter((e) => e !== email))
    toast({
      variant: 'success',
      title: 'Recipient removed.',
      action: (
        <Button
          variant='link'
          className='p-0'
          onClick={() => {
            addEmail(email)
          }}
        >
          Undo
        </Button>
      ),
    })
  }

  const hasRecipients = recipientsEmails.length > 0

  return (
    <Modal
      open={open}
      size='medium'
      title='Send one-time reminder'
      subtitle='Selected recipients will receive a one-time reminder to complete the questionnaire.'
      primaryButtonText={hasRecipients ? 'Send reminder' : 'Done'}
      primaryButtonProps={{ disabled: hasRecipients ? selectedEmails.length === 0 : false }}
      onOk={async () => {
        if (hasRecipients) {
          try {
            await sendReminder({
              orgCompanyId: companyId,
              emails: selectedEmails,
            })
            toast({
              variant: 'success',
              title: 'Reminder sent',
              description: 'An email has been sent to the selected recipients.',
            })
            onClose()
          } catch (error) {
            toast({
              variant: 'destructive',
              title: 'Failed to send reminder',
              description: error instanceof Error ? error.message : 'An error occurred',
            })
          }
        } else {
          onClose()
        }
      }}
      showCancelButton={hasRecipients}
      onCancel={onClose}
    >
      Send reminder to:
      <div className='my-2 flex flex-wrap gap-2'>
        {recipientsEmails.map((email) => {
          const isSelected = selectedEmails.includes(email)
          return (
            <UserBadge
              key={email}
              name={email}
              tooltipDisabled
              className='rounded-full'
              disabled={!isSelected}
              onRemove={() => removeEmail(email)}
              onRestore={() => addEmail(email)}
            />
          )
        })}
        {!hasRecipients && (
          <>
            <div className='text-main-foreground-tertiary text-body-default'>
              No recipients configured.
            </div>
            <Alert
              className='mt-5.5'
              icon={<AlertIcon icon={InfoIcon} />}
              variant='info'
              description='Add at least one recipient to your questionnaires.'
            />
          </>
        )}
      </div>
    </Modal>
  )
}
